import {
  MD_CASE_WORKFLOW_DOMAIN,
  type MdCase,
  type MdCaseAlertItem,
  type MdCasePiTagReference,
} from 'features/mdCases/mdCase'
import {
  buildMdCasePiTagReferenceId,
  deriveLegacyPiTagFromReferences,
} from 'features/mdCases/mdCaseReferences'
import { type RolodexEntryPutJson, RolodexEntryType } from 'types'

const DEFAULT_BLANK_MD_CASE_TITLE = 'Untitled M&D Case'

type MdCaseEntryAlertItem = Omit<MdCaseAlertItem, 'title' | 'severity'> & {
  title?: MdCaseAlertItem['title']
  severity?: MdCaseAlertItem['severity']
}

type MdCaseEntryNotes = {
  working_notes_b64: string
  recommendation_draft_b64: string
  published_recommendation_b64: string
  recommendation_summary: string
}

type MdCaseEntryAgent = Omit<MdCase['agent'], 'recommendation_rationale'> & {
  recommendation_rationale_b64: string | null
}

type MdCaseEntryData = Pick<
  MdCase,
  'meta' | 'workflow' | 'references' | 'annotations'
> & {
  alerts: {
    alert_ids: string[]
    items: MdCaseEntryAlertItem[]
  }
  notes: MdCaseEntryNotes
  agent: MdCaseEntryAgent
}

type MdCaseEntryTags = {
  workflow_domain: typeof MD_CASE_WORKFLOW_DOMAIN
  site: string
  organization: string
}

export type MdCaseDraftAlertInput = Pick<
  MdCaseAlertItem,
  'id' | 'pi_tag_id' | 'start_at' | 'end_at' | 'summary'
> & {
  pi_tag_ids?: MdCaseAlertItem['pi_tag_ids']
  focus_start_at?: MdCaseAlertItem['focus_start_at']
  focus_end_at?: MdCaseAlertItem['focus_end_at']
  title?: MdCaseAlertItem['title']
  severity?: MdCaseAlertItem['severity']
  chart_id?: string | null
  added_by?: string | null
  added_at?: string | null
}

interface BuildMdCaseEntryFromDraftAlertsArgs {
  id: string
  siteID: string
  organizationID: string
  title?: string | null
  draftAlerts: readonly MdCaseDraftAlertInput[]
}

export const buildMdCaseEntryFromDraftAlerts = ({
  id,
  siteID,
  organizationID,
  title,
  draftAlerts,
}: BuildMdCaseEntryFromDraftAlertsArgs): RolodexEntryPutJson<
  MdCaseEntryData,
  MdCaseEntryTags
> => {
  const alerts = draftAlerts.map(toMdCaseAlertItem)
  const piTagItems = derivePiTagReferencesFromDraftAlerts(draftAlerts)
  const derivedTitle =
    title?.trim() ||
    draftAlerts.find((alert) => alert.title?.trim())?.title?.trim() ||
    draftAlerts.find((alert) => alert.summary.trim())?.summary.trim() ||
    DEFAULT_BLANK_MD_CASE_TITLE

  return {
    id,
    type: RolodexEntryType.CASE,
    tags: {
      workflow_domain: MD_CASE_WORKFLOW_DOMAIN,
      site: siteID,
      organization: organizationID,
    },
    data: {
      meta: {
        title: derivedTitle,
        source_type: 'manual',
        workflow_mode: 'watch',
      },
      workflow: {
        case_status: 'Open',
        severity: 'Medium',
        promotion_state: 'roc_only',
        published_at: null,
        published_by: null,
        opened_at: null,
        opened_by_user_id: null,
        resolved_at: null,
        resolved_by_user_id: null,
        closed_at: null,
        closed_by_user_id: null,
        cleared_at: null,
        cleared_by_user_id: null,
        shelved_at: null,
        shelved_by_user_id: null,
        reopen_reason: null,
      },
      alerts: {
        alert_ids: dedupeStrings(alerts.map((alert) => alert.id)),
        items: alerts,
      },
      references: {
        pi_tag: deriveLegacyPiTagFromReferences(piTagItems),
        documents: [],
        pi_tag_items: piTagItems,
      },
      annotations: {
        items: [],
      },
      // Notes/rationale go through base64; see `caseFieldCodec`.  Plain
      // fields are intentionally omitted from outbound payloads.
      notes: {
        working_notes_b64: '',
        recommendation_draft_b64: '',
        published_recommendation_b64: '',
        recommendation_summary: '',
      },
      agent: {
        thread_id: null,
        feedback: null,
        recommendation_rationale_b64: null,
      },
    },
  }
}

function toMdCaseAlertItem(
  draftAlert: MdCaseDraftAlertInput
): MdCaseEntryAlertItem {
  return {
    id: draftAlert.id,
    pi_tag_id: draftAlert.pi_tag_id,
    pi_tag_ids: dedupeStrings(
      draftAlert.pi_tag_ids?.length
        ? draftAlert.pi_tag_ids
        : draftAlert.pi_tag_id
          ? [draftAlert.pi_tag_id]
          : []
    ),
    start_at: draftAlert.start_at,
    end_at: draftAlert.end_at,
    focus_start_at: draftAlert.focus_start_at ?? null,
    focus_end_at: draftAlert.focus_end_at ?? null,
    summary: draftAlert.summary,
    title: draftAlert.title,
    severity: draftAlert.severity,
  }
}

function derivePiTagReferencesFromDraftAlerts(
  draftAlerts: readonly MdCaseDraftAlertInput[]
): MdCasePiTagReference[] {
  const referencesByKey = new Map<string, MdCasePiTagReference>()

  draftAlerts.forEach((draftAlert) => {
    const piTagIDs = dedupeStrings(
      draftAlert.pi_tag_ids?.length
        ? draftAlert.pi_tag_ids
        : draftAlert.pi_tag_id
          ? [draftAlert.pi_tag_id]
          : []
    )

    piTagIDs.forEach((piTagID) => {
      const referenceKey = [
        piTagID,
        draftAlert.start_at ?? '',
        draftAlert.end_at ?? '',
      ].join('::')

      if (referencesByKey.has(referenceKey)) {
        return
      }

      referencesByKey.set(referenceKey, {
        id: buildMdCasePiTagReferenceId(
          piTagID,
          draftAlert.start_at,
          draftAlert.end_at
        ),
        pi_tag_id: piTagID,
        datapoint_entry_id: null,
        start_at: draftAlert.start_at,
        end_at: draftAlert.end_at,
        chart_id: draftAlert.chart_id ?? null,
        added_by: draftAlert.added_by ?? null,
        added_at: draftAlert.added_at ?? null,
      })
    })
  })

  return [...referencesByKey.values()]
}

function dedupeStrings(values: readonly string[]): string[] {
  const dedupedValues: string[] = []
  const seenValues = new Set<string>()

  values.forEach((value) => {
    if (seenValues.has(value)) {
      return
    }

    seenValues.add(value)
    dedupedValues.push(value)
  })

  return dedupedValues
}
